import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiFilePdfBold,
    PiImageBold,
    PiMusicNotesBold,
    PiPaperclipBold,
    PiPlusBold,
    PiTrashBold,
} from 'react-icons/pi';
import { Button, Input } from '@v2/design-system';
import FileDropzone from '@v2/components/ui/FileDropzone';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { CommissionModifierItem, CommissionPreview } from '../types';
import { formatBrl } from '../utils';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';

type EditableModifier = {
    key: string;
    name: string;
    delta_pp: number;
    kept: boolean;
    isNew?: boolean;
};

type ModifierChange = { name: string; delta_pp: number };

export type EditHistoryItem = {
    id: number;
    created_at: string | null;
    changed_by: { id: number; name: string } | null;
    reason: string | null;
    old_rate_pp: number | null;
    result_rate_pp: number | null;
    old_final_commission: number | null;
    final_commission: number | null;
    old_base_rate_pp: number | null;
    base_rate_pp: number | null;
    removed_deflators: ModifierChange[];
    added_deflators: ModifierChange[];
    removed_accelerators: ModifierChange[];
    added_accelerators: ModifierChange[];
    attachment_url: string | null;
    attachment_name: string | null;
    attachment_kind: 'image' | 'pdf' | 'audio' | 'file' | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    dealId: number;
    preview: CommissionPreview;
};

const ACCEPT =
    '.pdf,.jpg,.jpeg,.png,.webp,.mp3,application/pdf,image/jpeg,image/png,image/webp,audio/mpeg,audio/mp3';

function toEditable(items: CommissionModifierItem[] | undefined, prefix: string): EditableModifier[] {
    return (items ?? []).map((item, index) => ({
        key: `${prefix}-${index}-${item.name}`,
        name: item.name,
        delta_pp: Number(item.delta_pp ?? 0),
        kept: true,
    }));
}

function formatPp(value: number): string {
    return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
}

function formatDeltaPp(value: number): string {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} p.p.`;
}

function kindFromFile(file: File): 'image' | 'pdf' | 'audio' | 'file' {
    const type = (file.type || '').toLowerCase();
    const name = file.name.toLowerCase();
    if (type.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/.test(name)) return 'image';
    if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (type.startsWith('audio/') || /\.(mp3|mpeg|wav|ogg)$/.test(name)) return 'audio';
    return 'file';
}

export default function DealCommissionEditModal({ open, onClose, dealId, preview }: Props) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [baseRatePp, setBaseRatePp] = useState(Number(preview.base_rate_pp ?? 0));
    const [deflators, setDeflators] = useState<EditableModifier[]>(() => toEditable(preview.deflators, 'd'));
    const [accelerators, setAccelerators] = useState<EditableModifier[]>(() => toEditable(preview.accelerators, 'a'));
    const [reason, setReason] = useState('');
    const [attachment, setAttachment] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [historyPreview, setHistoryPreview] = useState<EditHistoryItem | null>(null);

    useEffect(() => {
        if (!open) return;
        setBaseRatePp(Number(preview.base_rate_pp ?? 0));
        setDeflators(toEditable(preview.deflators, 'd'));
        setAccelerators(toEditable(preview.accelerators, 'a'));
        setReason('');
        setAttachment(null);
        setHistoryPreview(null);
    }, [open, preview]);

    useEffect(() => {
        if (!attachment) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(attachment);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [attachment]);

    const historyQuery = useQuery({
        queryKey: ['commission-manual-edits', dealId],
        queryFn: async () => {
            const { data } = await api.get<{ data: EditHistoryItem[] }>(
                `/commissioning/deals/${dealId}/manual-edits`,
            );
            return data.data;
        },
        enabled: open && Boolean(dealId),
        staleTime: 15_000,
    });

    const keptDeflators = useMemo(() => deflators.filter((d) => d.kept), [deflators]);
    const keptAccelerators = useMemo(() => accelerators.filter((a) => a.kept), [accelerators]);
    const bonusRatePp = Number(preview.bonus_rate_pp ?? 0) || 0;

    const newRatePp = useMemo(() => {
        const defl = keptDeflators.reduce((sum, d) => sum + d.delta_pp, 0);
        const acc = keptAccelerators.reduce((sum, a) => sum + a.delta_pp, 0);
        return Math.round((baseRatePp + defl + acc + bonusRatePp) * 10000) / 10000;
    }, [baseRatePp, keptDeflators, keptAccelerators, bonusRatePp]);

    const oldRatePp =
        Number(preview.rate_with_exceptions_pp ?? preview.result_rate_pp ?? preview.base_rate_pp ?? 0) || 0;
    const oldFinal = Number(preview.final_commission ?? preview.paid_commission_amount ?? 0);
    const estimatedFinal =
        oldRatePp > 0.00001
            ? Math.round(oldFinal * (newRatePp / oldRatePp) * 100) / 100
            : Math.round(((Number(preview.deal_amount ?? 0) * newRatePp) / 100) * 100) / 100;

    const saveMutation = useMutation({
        mutationFn: async () => {
            const form = new FormData();
            form.append('reason', reason.trim());
            form.append('base_rate_pp', String(baseRatePp));
            form.append(
                'deflators',
                JSON.stringify(keptDeflators.map((d) => ({ name: d.name, delta_pp: d.delta_pp }))),
            );
            form.append(
                'accelerators',
                JSON.stringify(keptAccelerators.map((a) => ({ name: a.name, delta_pp: a.delta_pp }))),
            );
            if (attachment) {
                form.append('attachment', attachment);
            }

            return api.post(`/commissioning/deals/${dealId}/manual-edit`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: async () => {
            toast.success(t('v2.ploomes_deals.commission_edit.toast_ok'));
            await invalidateDealCommissionQueries(queryClient, dealId);
            onClose();
        },
        onError: (err: unknown) => {
            const status = (err as { response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } } })
                ?.response?.status;
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response
                ?.data;
            if (status === 419) {
                toast.error(t('v2.ploomes_deals.legacy_remediate.toast_csrf'));
                return;
            }
            const firstError = data?.errors ? Object.values(data.errors).flat()[0] : null;
            toast.error(firstError || data?.message || t('v2.ploomes_deals.commission_edit.toast_error'));
        },
    });

    const canSave = reason.trim().length >= 5 && !saveMutation.isPending && newRatePp >= 0;
    const localKind = attachment ? kindFromFile(attachment) : null;

    return (
        <>
            <V2Modal
                open={open}
                onClose={onClose}
                size="2xl"
                title={t('v2.ploomes_deals.commission_edit.title')}
                description={t('v2.ploomes_deals.commission_edit.description')}
                footer={
                    <>
                        <Button type="button" variant="outline" onClick={onClose} disabled={saveMutation.isPending}>
                            {t('v2.ploomes_deals.commission_edit.cancel')}
                        </Button>
                        <Button
                            type="button"
                            color="primary"
                            isLoading={saveMutation.isPending}
                            disabled={!canSave}
                            onClick={() => saveMutation.mutate()}
                        >
                            {t('v2.ploomes_deals.commission_edit.save')}
                        </Button>
                    </>
                }
            >
                <div className="space-y-5 text-sm">
                    <div className="grid gap-3 sm:grid-cols-3">
                        <label className="block space-y-1 sm:col-span-1">
                            <span className="text-xs font-medium text-gray-500 dark:text-zinc-400">
                                {t('v2.ploomes_deals.commission_edit.base_rate')}
                            </span>
                            <Input
                                type="number"
                                step="0.1"
                                min={0}
                                max={100}
                                value={Number.isFinite(baseRatePp) ? baseRatePp : 0}
                                onChange={(e) => setBaseRatePp(Number(e.target.value))}
                            />
                        </label>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/60">
                            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                {t('v2.ploomes_deals.commission_edit.new_rate')}
                            </p>
                            <p className="text-lg font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                                {formatPp(newRatePp)}
                            </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800/60">
                            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                {t('v2.ploomes_deals.commission_edit.estimated')}
                            </p>
                            <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-zinc-100">
                                {formatBrl(estimatedFinal)}
                            </p>
                        </div>
                    </div>

                    <ModifierList
                        title={t('v2.ploomes_deals.commission_edit.deflators')}
                        empty={t('v2.ploomes_deals.commission_edit.no_deflators')}
                        items={deflators}
                        onToggle={(key) =>
                            setDeflators((prev) => prev.map((d) => (d.key === key ? { ...d, kept: !d.kept } : d)))
                        }
                        onDeleteNew={(key) => setDeflators((prev) => prev.filter((d) => d.key !== key))}
                        onAdd={(name, deltaPp) => {
                            const abs = Math.abs(deltaPp);
                            if (abs < 0.00001) return;
                            setDeflators((prev) => [
                                ...prev,
                                {
                                    key: `d-new-${Date.now()}-${prev.length}`,
                                    name,
                                    delta_pp: -abs,
                                    kept: true,
                                    isNew: true,
                                },
                            ]);
                        }}
                        addLabel={t('v2.ploomes_deals.commission_edit.add_deflator')}
                        namePlaceholder={t('v2.ploomes_deals.commission_edit.add_name_ph')}
                        deltaPlaceholder={t('v2.ploomes_deals.commission_edit.add_delta_ph')}
                        deltaLabel={t('v2.ploomes_deals.commission_edit.add_delta_label')}
                        removeLabel={t('v2.ploomes_deals.commission_edit.remove')}
                        keepLabel={t('v2.ploomes_deals.commission_edit.keep')}
                        discardLabel={t('v2.ploomes_deals.commission_edit.discard')}
                    />

                    <ModifierList
                        title={t('v2.ploomes_deals.commission_edit.accelerators')}
                        empty={t('v2.ploomes_deals.commission_edit.no_accelerators')}
                        items={accelerators}
                        onToggle={(key) =>
                            setAccelerators((prev) => prev.map((a) => (a.key === key ? { ...a, kept: !a.kept } : a)))
                        }
                        onDeleteNew={(key) => setAccelerators((prev) => prev.filter((a) => a.key !== key))}
                        onAdd={(name, deltaPp) => {
                            const abs = Math.abs(deltaPp);
                            if (abs < 0.00001) return;
                            setAccelerators((prev) => [
                                ...prev,
                                {
                                    key: `a-new-${Date.now()}-${prev.length}`,
                                    name,
                                    delta_pp: abs,
                                    kept: true,
                                    isNew: true,
                                },
                            ]);
                        }}
                        addLabel={t('v2.ploomes_deals.commission_edit.add_accelerator')}
                        namePlaceholder={t('v2.ploomes_deals.commission_edit.add_name_ph')}
                        deltaPlaceholder={t('v2.ploomes_deals.commission_edit.add_delta_ph')}
                        deltaLabel={t('v2.ploomes_deals.commission_edit.add_delta_label')}
                        removeLabel={t('v2.ploomes_deals.commission_edit.remove')}
                        keepLabel={t('v2.ploomes_deals.commission_edit.keep')}
                        discardLabel={t('v2.ploomes_deals.commission_edit.discard')}
                    />

                    <label className="block space-y-1">
                        <span className="text-xs font-medium text-gray-500 dark:text-zinc-400">
                            {t('v2.ploomes_deals.commission_edit.reason')} *
                        </span>
                        <textarea
                            className="min-h-[88px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none ring-0 focus:border-sky-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('v2.ploomes_deals.commission_edit.reason_placeholder')}
                            maxLength={2000}
                        />
                    </label>

                    <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-500 dark:text-zinc-400">
                            {t('v2.ploomes_deals.commission_edit.attachment')}
                        </p>
                        <FileDropzone
                            accept={ACCEPT}
                            acceptLabel={t('v2.ploomes_deals.commission_edit.attachment_accept')}
                            maxSizeMb={10}
                            label={t('v2.ploomes_deals.commission_edit.drop_label')}
                            hint={t('v2.ploomes_deals.commission_edit.drop_hint')}
                            showSelected={false}
                            disabled={saveMutation.isPending}
                            onFiles={(files) => setAttachment(files[0] ?? null)}
                            onError={(msg) => toast.error(msg)}
                            zoneClassName="py-8"
                        />
                        {attachment && previewUrl && localKind && (
                            <AttachmentPreview
                                kind={localKind}
                                url={previewUrl}
                                name={attachment.name}
                                onClear={() => setAttachment(null)}
                                clearLabel={t('v2.ploomes_deals.commission_edit.clear_file')}
                            />
                        )}
                    </div>

                    <ChangeLogPanel
                        loading={historyQuery.isPending}
                        items={historyQuery.data ?? []}
                        emptyLabel={t('v2.ploomes_deals.commission_edit.history_empty')}
                        loadingLabel={t('v2.ploomes_deals.commission_edit.history_loading')}
                        title={t('v2.ploomes_deals.commission_edit.history')}
                        onOpenAttachment={(item) => setHistoryPreview(item)}
                    />
                </div>
            </V2Modal>

            <V2Modal
                open={historyPreview !== null && Boolean(historyPreview?.attachment_url)}
                onClose={() => setHistoryPreview(null)}
                size="2xl"
                title={historyPreview?.attachment_name ?? t('v2.ploomes_deals.commission_edit.attachment')}
                description={historyPreview?.changed_by?.name ?? undefined}
                footer={
                    <Button type="button" variant="outline" onClick={() => setHistoryPreview(null)}>
                        {t('v2.ploomes_deals.commission_edit.cancel')}
                    </Button>
                }
            >
                {historyPreview?.attachment_url && historyPreview.attachment_kind && (
                    <AttachmentPreview
                        kind={historyPreview.attachment_kind}
                        url={historyPreview.attachment_url}
                        name={historyPreview.attachment_name ?? 'anexo'}
                        embedded
                    />
                )}
            </V2Modal>
        </>
    );
}

export function ChangeLogPanel({
    title,
    loading,
    items,
    emptyLabel,
    loadingLabel,
    onOpenAttachment,
    className,
}: {
    title: string;
    loading: boolean;
    items: EditHistoryItem[];
    emptyLabel: string;
    loadingLabel: string;
    onOpenAttachment?: (item: EditHistoryItem) => void;
    className?: string;
}) {
    const { t } = useI18n();

    return (
        <div className={cn('rounded-xl border border-gray-200 dark:border-zinc-700', className)}>
            <div className="border-b border-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 dark:border-zinc-700 dark:text-zinc-200">
                {title}
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto px-3 py-2">
                {loading && <p className="text-xs text-gray-400">{loadingLabel}</p>}
                {!loading && items.length === 0 && <p className="text-xs text-gray-400">{emptyLabel}</p>}
                {items.map((item) => (
                    <article
                        key={item.id}
                        className="rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2 text-xs dark:border-zinc-700/80 dark:bg-zinc-800/50"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-semibold text-gray-800 dark:text-zinc-100">
                                {item.changed_by?.name ?? '—'}
                            </p>
                            <span className="text-[11px] text-gray-400">
                                {item.created_at ? new Date(item.created_at).toLocaleString('pt-BR') : '—'}
                            </span>
                        </div>
                        <p className="mt-1 text-gray-600 dark:text-zinc-300">{item.reason}</p>

                        <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                            {(item.old_rate_pp != null || item.result_rate_pp != null) && (
                                <p className="rounded bg-white px-2 py-1 tabular-nums dark:bg-zinc-900/70">
                                    <span className="text-gray-400">{t('v2.ploomes_deals.commission_edit.log_rate')}: </span>
                                    {item.old_rate_pp != null && (
                                        <span className="text-gray-400 line-through">{formatPp(Number(item.old_rate_pp))}</span>
                                    )}
                                    {item.result_rate_pp != null && (
                                        <span className="ml-1 font-semibold text-emerald-700 dark:text-emerald-400">
                                            {formatPp(Number(item.result_rate_pp))}
                                        </span>
                                    )}
                                </p>
                            )}
                            {(item.old_final_commission != null || item.final_commission != null) && (
                                <p className="rounded bg-white px-2 py-1 tabular-nums dark:bg-zinc-900/70">
                                    <span className="text-gray-400">{t('v2.ploomes_deals.commission_edit.log_amount')}: </span>
                                    {item.old_final_commission != null && (
                                        <span className="text-gray-400 line-through">
                                            {formatBrl(Number(item.old_final_commission))}
                                        </span>
                                    )}
                                    {item.final_commission != null && (
                                        <span className="ml-1 font-semibold text-gray-900 dark:text-zinc-100">
                                            {formatBrl(Number(item.final_commission))}
                                        </span>
                                    )}
                                </p>
                            )}
                        </div>

                        <ModifierChangeList
                            label={t('v2.ploomes_deals.commission_edit.log_removed_deflators')}
                            items={item.removed_deflators}
                            tone="rose"
                        />
                        <ModifierChangeList
                            label={t('v2.ploomes_deals.commission_edit.log_added_deflators')}
                            items={item.added_deflators}
                            tone="emerald"
                        />
                        <ModifierChangeList
                            label={t('v2.ploomes_deals.commission_edit.log_removed_accelerators')}
                            items={item.removed_accelerators}
                            tone="rose"
                        />
                        <ModifierChangeList
                            label={t('v2.ploomes_deals.commission_edit.log_added_accelerators')}
                            items={item.added_accelerators}
                            tone="emerald"
                        />

                        {item.attachment_url && (
                            <button
                                type="button"
                                onClick={() => onOpenAttachment?.(item)}
                                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-800 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                            >
                                {item.attachment_kind === 'image' && <PiImageBold className="h-3.5 w-3.5" />}
                                {item.attachment_kind === 'pdf' && <PiFilePdfBold className="h-3.5 w-3.5" />}
                                {item.attachment_kind === 'audio' && <PiMusicNotesBold className="h-3.5 w-3.5" />}
                                {!item.attachment_kind || item.attachment_kind === 'file' ? (
                                    <PiPaperclipBold className="h-3.5 w-3.5" />
                                ) : null}
                                {t('v2.ploomes_deals.commission_edit.preview_attachment')}
                                {item.attachment_name ? ` · ${item.attachment_name}` : ''}
                            </button>
                        )}
                    </article>
                ))}
            </div>
        </div>
    );
}

function ModifierChangeList({
    label,
    items,
    tone,
}: {
    label: string;
    items: ModifierChange[];
    tone: 'rose' | 'emerald';
}) {
    if (!items?.length) return null;
    return (
        <div className="mt-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
            <ul className="mt-0.5 space-y-0.5">
                {items.map((item) => (
                    <li
                        key={`${item.name}-${item.delta_pp}`}
                        className={cn(
                            'rounded px-1.5 py-0.5',
                            tone === 'rose'
                                ? 'bg-rose-50 text-rose-800 dark:bg-rose-950/30 dark:text-rose-200'
                                : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200',
                        )}
                    >
                        {item.name}{' '}
                        <span className="tabular-nums opacity-80">({formatDeltaPp(item.delta_pp)})</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function AttachmentPreview({
    kind,
    url,
    name,
    onClear,
    clearLabel,
    embedded = false,
}: {
    kind: 'image' | 'pdf' | 'audio' | 'file';
    url: string;
    name: string;
    onClear?: () => void;
    clearLabel?: string;
    embedded?: boolean;
}) {
    const { t } = useI18n();

    return (
        <div
            className={cn(
                'overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
                !embedded && 'mt-1',
            )}
        >
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
                <p className="min-w-0 truncate text-xs font-medium text-gray-700 dark:text-zinc-200">{name}</p>
                <div className="flex shrink-0 items-center gap-2">
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] font-medium text-sky-700 hover:underline dark:text-sky-300"
                    >
                        {t('v2.ploomes_deals.commission_edit.open_file')}
                    </a>
                    {onClear && (
                        <button
                            type="button"
                            onClick={onClear}
                            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                        >
                            <PiTrashBold className="h-3 w-3" />
                            {clearLabel ?? t('v2.ploomes_deals.commission_edit.clear_file')}
                        </button>
                    )}
                </div>
            </div>
            <div className="bg-gray-50 p-2 dark:bg-zinc-950/40">
                {kind === 'image' && (
                    <img src={url} alt={name} className="mx-auto max-h-64 rounded-lg object-contain" />
                )}
                {kind === 'pdf' && (
                    <iframe title={name} src={url} className="h-72 w-full rounded-lg border border-gray-200 dark:border-zinc-700" />
                )}
                {kind === 'audio' && (
                    <div className="flex flex-col items-center gap-3 px-3 py-6">
                        <PiMusicNotesBold className="h-8 w-8 text-sky-600 dark:text-sky-400" />
                        <audio controls src={url} className="w-full max-w-md">
                            <track kind="captions" />
                        </audio>
                    </div>
                )}
                {kind === 'file' && (
                    <p className="px-3 py-6 text-center text-xs text-gray-500">
                        {t('v2.ploomes_deals.commission_edit.preview_unavailable')}
                    </p>
                )}
            </div>
        </div>
    );
}

function ModifierList({
    title,
    empty,
    items,
    onToggle,
    onDeleteNew,
    onAdd,
    addLabel,
    namePlaceholder,
    deltaPlaceholder,
    deltaLabel,
    removeLabel,
    keepLabel,
    discardLabel,
}: {
    title: string;
    empty: string;
    items: EditableModifier[];
    onToggle: (key: string) => void;
    onDeleteNew: (key: string) => void;
    onAdd: (name: string, deltaPp: number) => void;
    addLabel: string;
    namePlaceholder: string;
    deltaPlaceholder: string;
    deltaLabel: string;
    removeLabel: string;
    keepLabel: string;
    discardLabel: string;
}) {
    const [name, setName] = useState('');
    const [deltaRaw, setDeltaRaw] = useState('');

    const handleAdd = () => {
        const trimmed = name.trim();
        const delta = Number(String(deltaRaw).replace(',', '.'));
        if (!trimmed || !Number.isFinite(delta) || Math.abs(delta) < 0.00001) {
            return;
        }
        onAdd(trimmed, delta);
        setName('');
        setDeltaRaw('');
    };

    return (
        <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                {title}
            </p>
            {items.length === 0 ? (
                <p className="text-xs text-gray-400">{empty}</p>
            ) : (
                <ul className="space-y-1.5">
                    {items.map((item) => (
                        <li
                            key={item.key}
                            className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 ${
                                item.kept
                                    ? item.isNew
                                        ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/50 dark:bg-emerald-950/30'
                                        : 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900'
                                    : 'border-rose-200 bg-rose-50/70 opacity-70 dark:border-rose-900/50 dark:bg-rose-950/30'
                            }`}
                        >
                            <div className="min-w-0">
                                <p className={`truncate text-sm font-medium ${item.kept ? '' : 'line-through'}`}>
                                    {item.name}
                                </p>
                                <p className="text-xs tabular-nums text-gray-500">{formatDeltaPp(item.delta_pp)}</p>
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="shrink-0"
                                onClick={() => (item.isNew ? onDeleteNew(item.key) : onToggle(item.key))}
                            >
                                <PiTrashBold className="mr-1 h-3.5 w-3.5" />
                                {item.isNew ? discardLabel : item.kept ? removeLabel : keepLabel}
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-gray-200 p-2.5 sm:flex-row sm:items-end dark:border-zinc-700">
                <label className="min-w-0 flex-1 space-y-1">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                        {addLabel}
                    </span>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={namePlaceholder}
                        className="w-full"
                    />
                </label>
                <label className="w-full space-y-1 sm:w-28">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                        {deltaLabel}
                    </span>
                    <Input
                        type="text"
                        inputMode="decimal"
                        value={deltaRaw}
                        onChange={(e) => setDeltaRaw(e.target.value)}
                        placeholder={deltaPlaceholder}
                        className="w-full tabular-nums"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAdd();
                            }
                        }}
                    />
                </label>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    disabled={!name.trim() || !deltaRaw.trim()}
                    onClick={handleAdd}
                >
                    <PiPlusBold className="mr-1 h-3.5 w-3.5" />
                    {addLabel}
                </Button>
            </div>
        </div>
    );
}
